import { Inline, P, Spinner, Stack } from "@adfenix/alexandria";
import { fillImageTemplate } from "api/services";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { IconButton } from "components/IconButton/IconButton";
import { SolidCopy } from "@adfenix/icons";
import { TemplateService } from "@adfenix/types";
import { useEffect, useState } from "react";

interface Props {
  subjectId: string | undefined;
  mainImageUrl: string | undefined;
  templateId: string;
  subjectType: TemplateService.SubjectType;
}

export const ImageTemplatePreview = ({
  mainImageUrl,
  templateId,
  subjectId,
  subjectType
}: Props) => {
  const [loadingImage, setLoadingImage] = useState(false);
  const {
    data: templateResponse,
    isError: fillTemplateError,
    isFetching: isLoadingTemplate
  } = useQuery(
    ["fillImageTemplates", templateId, subjectId],
    () =>
      fillImageTemplate({
        subjectId: subjectId!,
        subjectType,
        templateId,
        imageUrl: mainImageUrl!
      }),
    {
      enabled: Boolean(subjectId && mainImageUrl)
    }
  );

  // It can take some time to load the image the first time from cloudinary.
  useEffect(() => {
    setLoadingImage(true);
  }, [templateResponse?.url]);

  if (fillTemplateError) {
    toast.error("Failed to preview template");
    return null;
  }

  if (isLoadingTemplate) {
    return <Spinner />;
  }

  if (!templateResponse || !mainImageUrl || !subjectId) {
    return null;
  }

  return (
    <Stack pY="s" spacing="m">
      {templateResponse.url && (
        <Inline alignItems="center">
          <IconButton
            label="copy-image-template-url"
            onClick={() => {
              navigator.clipboard.writeText(templateResponse.url);
              toast.success("Copied image url to clipboard");
            }}
          >
            <SolidCopy />
          </IconButton>
          <P>Copy image url</P>
        </Inline>
      )}

      {loadingImage && <Spinner />}
      <img src={templateResponse.url} onLoad={() => setLoadingImage(false)} />
    </Stack>
  );
};
